import {ChangeEvent, FC, KeyboardEvent, useLayoutEffect, useRef, useState} from 'react';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {
	CARD_COMMISSION_HINT,
	CARD_NUMBER_PLACEHOLDER,
	extractCardDigits,
	formatCardDisplay,
} from '../../model/cardNumber';
import styles from './CardNumberStep.module.css';

type Props = {
	value: string;
	onChange: (cardDigits: string) => void;
};

const caretPosAfterDigits = (display: string, digitCount: number): number => {
	if (digitCount <= 0) {
		return 0;
	}

	let seen = 0;

	for (let i = 0; i < display.length; i++) {
		if (/\d/.test(display[i]!)) {
			seen += 1;

			if (seen === digitCount) {
				return i + 1;
			}
		}
	}

	return display.length;
};

export const CardNumberStep: FC<Props> = ({value, onChange}) => {
	const cardDigits = extractCardDigits(value);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [caretDigits, setCaretDigits] = useState<number | null>(null);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target;
		const nextRaw = input.value;
		const nextCard = extractCardDigits(nextRaw);
		const caret = input.selectionStart ?? nextRaw.length;
		const digitsBeforeCaret = extractCardDigits(nextRaw.slice(0, caret)).length;

		onChange(nextCard);
		setCaretDigits(digitsBeforeCaret);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Backspace') {
			return;
		}

		const input = event.currentTarget;
		const caret = input.selectionStart ?? 0;
		const selectionEnd = input.selectionEnd ?? 0;

		if (caret !== selectionEnd || caret === 0) {
			return;
		}

		const display = formatCardDisplay(cardDigits);
		const prevChar = display[caret - 1];

		if (prevChar !== ' ') {
			return;
		}

		event.preventDefault();

		const digitsBeforeCaret = extractCardDigits(display.slice(0, caret)).length;

		if (digitsBeforeCaret === 0) {
			return;
		}

		const nextDigits = cardDigits.slice(0, digitsBeforeCaret - 1) + cardDigits.slice(digitsBeforeCaret);
		onChange(nextDigits);
		setCaretDigits(digitsBeforeCaret - 1);
	};

	useLayoutEffect(() => {
		if (caretDigits === null || !inputRef.current) {
			return;
		}

		const pos = caretPosAfterDigits(formatCardDisplay(cardDigits), caretDigits);
		inputRef.current.setSelectionRange(pos, pos);
		setCaretDigits(null);
	}, [cardDigits, caretDigits]);

	return (
		<div className={styles.root}>
			<div className={styles.intro}>
				<h2 className={styles.title}>Введите номер карты</h2>
				<p className={styles.subtitle}>{CARD_COMMISSION_HINT}</p>
			</div>

			<div className={styles.field}>
				<TextInput
					ref={inputRef}
					name="paymentCard"
					className={styles.input}
					value={formatCardDisplay(value)}
					placeholder={CARD_NUMBER_PLACEHOLDER}
					inputMode="numeric"
					autoComplete="cc-number"
					autoFocus
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClear={() => onChange('')}
				/>
			</div>
		</div>
	);
};
